import { Link } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/base/Seo";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SubscribeForm from "@/components/feature/SubscribeForm";
import { principles, testimonials, milestones } from "@/mocks/site";

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于看料88",
      inLanguage: "zh-CN",
      url: `${SITE_URL}/about`,
      description:
        "看料88是专注娱乐话题回顾的存档型内容站，介绍品牌定位、内容原则、发展理念与读者口碑。",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "看料88",
      url: SITE_URL,
      email: "hello@kanliao88.example.com",
      description: "看料88，娱乐话题回顾与往期存档站。",
      foundingLocation: "中国",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_URL}/about` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="关于我们 | 看料88 娱乐话题存档站的内容原则"
        description="了解看料88的定位、发展理念与内容原则：只做公开信息梳理，按时间线归档娱乐话题，长期留存往期内容，为读者提供经得起回看的话题回顾。"
        keywords="关于看料88,看料88内容原则,娱乐话题存档,品牌介绍,发展理念"
        path="/about"
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center lg:gap-12">
            <div className="lg:col-span-7">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
                About 看料88
              </p>
              <h1 className="mt-3 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                把话题放回时间里，才看得清它本来的样子
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                看料88 是一个娱乐话题回顾与存档型内容站。我们不做即时的情绪放大器，而是把综艺、影视、创作者与网络热议按发布时间整理归档，让读者可以随时翻回某一段时间，重新看清讨论的来龙去脉。
              </p>
              <div className="mt-6 grid max-w-xl grid-cols-3 gap-3">
                {[
                  { value: "6", label: "常设栏目" },
                  { value: "10+", label: "已归档话题" },
                  { value: "长期", label: "内容留存" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                    <p className="font-heading text-xl font-bold text-primary-600">{stat.value}</p>
                    <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-72">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cream%20and%20amber%20abstract%20archive%20of%20stacked%20paper%20documents%20with%20soft%20window%20light%2C%20no%20people%2C%20cozy%20editorial%20still%20life%2C%20minimal%20composition%20with%20terracotta%20accents&width=1000&height=800&seq=kl-about-01&orientation=landscape"
                  alt="看料88 话题存档整理示意图"
                  title="看料88 娱乐话题存档 - 内容整理"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section aria-labelledby="principles-heading" className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full px-4 py-12 md:px-8 md:py-16">
          <div className="max-w-2xl">
            <h2 id="principles-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              我们的内容原则
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              这四条原则决定了我们写什么、怎么写，也决定了哪些内容我们不会碰。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-xl text-primary-600">
                  <i className={item.icon} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="milestone-heading" className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-12 md:px-8 md:py-16">
          <div className="max-w-2xl">
            <h2 id="milestone-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              我们从哪里来
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              从一份个人话题清单，到一套可检索的存档体系，站点的发展思路一直没有变。
            </p>
          </div>
          <ol className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {milestones.map((item, index) => (
              <li key={item.title} className="relative rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-500 text-xs font-bold text-background-50">
                  {index + 1}
                </span>
                <p className="mt-3 font-heading text-sm font-semibold text-accent-700">{item.time}</p>
                <h3 className="mt-1 font-heading text-base font-semibold leading-snug text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section aria-labelledby="testimonial-heading" className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full px-4 py-12 md:px-8 md:py-16">
          <div className="max-w-2xl">
            <h2 id="testimonial-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              读者怎么说
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              来自读者的真实反馈，是我们持续整理存档的动力。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <i className="ri-double-quotes-l text-2xl text-accent-400" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.quote}
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-semibold text-secondary-800">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-950">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="contact-heading" className="bg-background-100">
        <div className="mx-auto w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-6">
              <h2 id="contact-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                联系我们
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                无论你是想订阅更新、提供内容线索，还是希望对某篇盘点提出不同看法，都欢迎通过下面的方式联系我们。
              </p>
              <ul className="mt-6 flex flex-col gap-4">
                <li className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-lg text-primary-600">
                    <i className="ri-mail-line" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">联系邮箱</span>
                    <a
                      href="mailto:hello@kanliao88.example.com"
                      className="text-sm font-medium text-foreground-950 hover:text-primary-600"
                    >
                      hello@kanliao88.example.com
                    </a>
                  </span>
                </li>
                <li className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-lg text-accent-700">
                    <i className="ri-time-line" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">回复时间</span>
                    <span className="text-sm font-medium text-foreground-950">工作日 3 个工作日内回复</span>
                  </span>
                </li>
              </ul>
              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base font-semibold text-foreground-950">常见问题先看看</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  关于内容收录标准、查找方式与版权说明，我们已经在常见问题里做了详细说明。
                </p>
                <Link
                  to="/faq"
                  className="mt-3 inline-block whitespace-nowrap rounded-full border border-primary-300 px-4 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
                >
                  前往常见问题
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
                <h3 className="font-heading text-lg font-bold text-foreground-950">订阅更新 / 提供线索</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  留下邮箱即可订阅，也可以顺带留下你希望我们回顾的话题方向。
                </p>
                <div className="mt-5">
                  <SubscribeForm />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}